import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { Waves, Clock, Calendar } from 'lucide-react'

const PoolSettings = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm()

  // Fetch current pool settings
  const fetchSettings = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await axios.get('/api/pool-settings')
      reset(response.data)
    } catch (err) {
      setError('Failed to load pool settings. Please try again.')
      console.error('Error fetching pool settings:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setSubmitting(true)
      setError('')
      setSuccess('')

      // Sync pool hours: single field writes to both weekday and weekend
      data.weekendPoolHours = data.weekdayPoolHours

      await axios.put('/api/pool-settings', data)
      setSuccess('Pool settings updated successfully!')

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Failed to update pool settings: ' + (err.response?.data?.message || err.message))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Pool & Lifeguard Hours</h1>
        <p className="text-gray-600 mt-1">Manage pool hours and lifeguard schedules</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Season Dates */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <Calendar className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Season Dates</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opening Date
                </label>
                <input
                  type="text"
                  {...register('openingDate', { required: 'Opening date is required' })}
                  className={`w-full px-4 py-2 border ${
                    errors.openingDate ? 'border-red-300' : 'border-gray-300'
                  } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none`}
                  placeholder="May 24th, 2025"
                />
                {errors.openingDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.openingDate.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Closing Date
                </label>
                <input
                  type="text"
                  {...register('closingDate', { required: 'Closing date is required' })}
                  className={`w-full px-4 py-2 border ${
                    errors.closingDate ? 'border-red-300' : 'border-gray-300'
                  } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none`}
                  placeholder="Labor Day Weekend"
                />
                {errors.closingDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.closingDate.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Pool Hours */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <Waves className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Pool Hours</h2>
            </div>

            <div className="max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Hours
                  <span className="text-gray-500 font-normal ml-1">(same every day)</span>
                </label>
                <input
                  type="text"
                  {...register('weekdayPoolHours', { required: 'Pool hours are required' })}
                  className={`w-full px-4 py-2 border ${
                    errors.weekdayPoolHours ? 'border-red-300' : 'border-gray-300'
                  } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none`}
                  placeholder="10:00 AM - 9:00 PM"
                />
                {errors.weekdayPoolHours && (
                  <p className="mt-1 text-sm text-red-600">{errors.weekdayPoolHours.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Lifeguard Hours */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <Clock className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Lifeguard Hours</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weekday Hours
                  <span className="text-gray-500 font-normal ml-1">(Monday - Friday)</span>
                </label>
                <input
                  type="text"
                  {...register('weekdayLifeguardHours', { required: 'Weekday lifeguard hours are required' })}
                  className={`w-full px-4 py-2 border ${
                    errors.weekdayLifeguardHours ? 'border-red-300' : 'border-gray-300'
                  } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none`}
                  placeholder="4:00 PM - 8:00 PM"
                />
                {errors.weekdayLifeguardHours && (
                  <p className="mt-1 text-sm text-red-600">{errors.weekdayLifeguardHours.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weekend Hours
                  <span className="text-gray-500 font-normal ml-1">(Saturday & Sunday)</span>
                </label>
                <input
                  type="text"
                  {...register('weekendLifeguardHours', { required: 'Weekend lifeguard hours are required' })}
                  className={`w-full px-4 py-2 border ${
                    errors.weekendLifeguardHours ? 'border-red-300' : 'border-gray-300'
                  } rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none`}
                  placeholder="12:00 PM - 8:00 PM"
                />
                {errors.weekendLifeguardHours && (
                  <p className="mt-1 text-sm text-red-600">{errors.weekendLifeguardHours.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Additional Notes</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Special Instructions or Notes (Optional)
              </label>
              <textarea
                {...register('additionalNotes')}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                placeholder="e.g., Pool may close early during inclement weather..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

export default PoolSettings
